// Koppelingen · Taken (Koppelingen.dc.html, KoppelingenGekoppeld, KoppelingenLade): elke open Planner-taak hoort bij
// een Clockify-taak in het project van haar klantplan; alleen gekoppelde taken tellen mee in uren en bezetting.
// Koppelingen zijn n-op-n (Rens, 23/10): hier kiest u de Clockify-taak van een taak (dat vervangt wat er hing); meer
// Clockify-taken bij één taak zet u in het tabblad Uren van de taak, en een Clockify-taak mag bij meerdere taken.
// Drie weergaven: Te koppelen (beste voorstel op naam met de zekerheid, hoogste eerst), Gekoppeld (ook wat bewust geen
// Clockify-taak heeft) en Afsluiten (geen koppeling nodig). Taken van een klantplan zonder Clockify-project staan per
// plan apart: eerst het plan koppelen. Zonder goed voorstel maakt de app de Clockify-taak met de titel uit Planner.
// Een supporttaak in Clockify is nooit een voorstel: haar uren tellen als support, niet voor een Planner-taak.
import { useState } from "react";
import { clockifyApi, fmtHours, type ClockifyLinks, type TaskLinkRow } from "../lib/api-clockify";
import { Icon } from "../lib/icons";
import { ddmm, deadline } from "../lib/planning";
import type { PlanningData, Task } from "../lib/types";
import { ClockifyNotice, KoppelCard, KoppelList, OFF, OK, WARN, isSupport, pctOf, scorePill, useKoppelSave, type KRow } from "./KoppelList";
import { KoppelPicker, type PickOpt } from "./KoppelPicker";
import { PlanPicker, projText } from "./KoppelPlannen";

type View = "open" | "linked" | "skip";
type Conf = "all" | "hi" | "mid" | "lo" | "none";
type Sugg = TaskLinkRow["suggestions"][number];
interface Item { r: TaskLinkRow; t: Task | undefined; klant: string; who: string; best: Sugg | null; pct: number | null }

const VIEWS: [View, string][] = [["open", "Te koppelen"], ["linked", "Gekoppeld"], ["skip", "Afsluiten"]];
const CONF: [Conf, string][] = [["all", "Alle"], ["hi", "≥ 90 %"], ["mid", "80–89 %"], ["lo", "< 80 %"], ["none", "Geen voorstel"]];
const THRESHOLDS = [95, 90, 85, 80, 70];
const EST = [4, 8, 16, 24, 40];
/** zo noemt de server een gekoppelde Clockify-taak die niet meer bestaat */
const MISSING = "(verwijderd in Clockify)";
const NEW = "__nieuw", NONE = "__geen";
/** de server neemt hoogstens zoveel koppelingen per keer */
const CHUNK = 200;
const SUPPORT_NOTE = "voor supporturen, niet voor een Planner-taak";

const nOf = (x: number, one: string, many: string) => x + " " + (x === 1 ? one : many);
const viewOf = (r: TaskLinkRow): View => (r.linked || r.none ? "linked" : r.category === "afsluiten" ? "skip" : "open");
const confOf = (pct: number | null): Conf => (pct == null ? "none" : pct >= 90 ? "hi" : pct >= 80 ? "mid" : "lo");
/** wat er nu staat, om terug te zetten: de Clockify-taak, "" (bewust geen) of null (niets) */
const prevOf = (r: TaskLinkRow): string | null => (r.linked ? r.linked.clockifyTaskId : r.none ? "" : null);

async function linkAll(links: { taskId: string; clockifyTaskId: string | null }[]) {
  let last = { ok: true, text: "" };
  for (let i = 0; i < links.length; i += CHUNK) last = await clockifyApi.linkTasks(links.slice(i, i + CHUNK));
  return last;
}

export function KoppelTaken({ q, links, data }: { q: string; links: ClockifyLinks; data: PlanningData }) {
  const { save, busy } = useKoppelSave();
  const [view, setView] = useState<View>("open");
  const [klant, setKlant] = useState<string | null>(null);
  const [conf, setConf] = useState<Conf>("all");
  const [active, setActive] = useState(false);
  const [thr, setThr] = useState(90);
  const [dd, setDd] = useState(false);
  const [shown, setShown] = useState<Record<string, boolean>>({}); // uitgeklapte klantplannen zonder project
  const [pick, setPick] = useState<{ kind: "task" | "plan"; id: string } | null>(null);

  const people = new Map(data.people.map((p) => [p.id, p]));
  const tasks = new Map(data.tasks.map((t) => [t.id, t]));
  const klanten = new Map(data.klanten.map((k) => [k.id, k]));
  const plans = new Map(links.plans.map((p) => [p.projectId, p]));
  const kName = (pid: string) => klanten.get(pid)?.name || plans.get(pid)?.klant || "Onbekende klant";
  const planName = (pid: string) => klanten.get(pid)?.plan || "Planner " + kName(pid);
  const items: Item[] = links.tasks.map((r) => {
    const t = tasks.get(r.taskId);
    const best = r.suggestions.find((s) => !isSupport(s.name)) || null;
    const who = !t ? "" : t.owners.length ? t.owners.map((o) => people.get(o.personId)?.first || "?").join(", ") : "zonder eigenaar";
    return { r, t, klant: kName(r.projectId), who, best, pct: best ? pctOf(best.score) : null };
  });
  const sub = (i: Item) => [i.klant, i.who, i.t ? deadline(i.t.end, data.today).text : "", i.r.category === "geparkeerd" ? "geparkeerd" : ""].filter(Boolean).join(" · ");

  // ---------- schrijven ----------
  const linkOne = (r: TaskLinkRow, to: string | null) =>
    save(() => clockifyApi.linkTasks([{ taskId: r.taskId, clockifyTaskId: to }]), () => clockifyApi.linkTasks([{ taskId: r.taskId, clockifyTaskId: prevOf(r) }]));
  const create = (r: TaskLinkRow) => save(() => clockifyApi.createTask(r.taskId));

  // ---------- filteren ----------
  const qq = q.trim().toLowerCase();
  const scoped = (i: Item, v: View) => viewOf(i.r) === v && (!active || v === "skip" || i.r.category === "actief");
  const count = (v: View) => items.filter((i) => scoped(i, v) && (!klant || i.r.projectId === klant)).length;
  const list = items.filter((i) => scoped(i, view) && (!klant || i.r.projectId === klant)
    && (!qq || [i.r.title, i.klant, i.who, i.best?.name || "", i.r.linked?.name || ""].join(" ").toLowerCase().includes(qq)));
  const byTitle = (a: Item, b: Item) => a.r.title.localeCompare(b.r.title);

  const rows: KRow[] = [];
  let title = "", bulk: Item[] = [];
  if (view === "open") {
    const ready = list.filter((i) => i.r.planLinked && (conf === "all" || confOf(i.pct) === conf))
      .sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1) || byTitle(a, b));
    const waiting = conf === "all" ? list.filter((i) => !i.r.planLinked) : [];
    for (const i of ready) {
      const proj = plans.get(i.r.projectId)?.linked;
      const base = { key: i.r.taskId, cls: "p-kp-t", a: i.r.title, aSub: sub(i) };
      const other = { label: "Andere", title: "Zelf een Clockify-taak kiezen, een nieuwe maken of geen", run: () => setPick({ kind: "task", id: i.r.taskId }) };
      if (i.best && i.pct != null) {
        const best = i.best;
        rows.push({
          ...base, b: best.name, bCls: "mono",
          bSub: "Clockify-project " + (proj ? projText(proj) : "") + (best.estimate ? " · " + fmtHours(best.estimate) + " geschat" : ""),
          pill: i.pct + " %", pillCls: scorePill(i.pct),
          acts: [{ label: "Koppelen", primary: i.pct >= 90, run: () => linkOne(i.r, best.clockifyTaskId) }, other],
        });
      } else {
        rows.push({
          ...base, b: "Nieuwe Clockify-taak", bSub: "geen gelijkaardige taak in " + (proj ? projText(proj) : "het project"), pill: "Nieuw", pillCls: WARN,
          acts: [{ label: "Clockify-taak maken", title: "Maakt in Clockify een taak met deze titel, in het project van de klant, en koppelt ze", run: () => create(i.r) }, other],
        });
      }
    }
    // in bulk krijgt een voorstel hoogstens één taak, die met de hoogste zekerheid; zelf kiezen mag er meer (n-op-n)
    const seen = new Set<string>();
    bulk = ready.filter((i) => {
      if (!i.best || i.pct == null || i.pct < thr || seen.has(i.best.clockifyTaskId)) return false;
      seen.add(i.best.clockifyTaskId);
      return true;
    });
    const groups = new Map<string, Item[]>();
    for (const i of waiting) groups.set(i.r.projectId, [...(groups.get(i.r.projectId) || []), i]);
    if (groups.size) rows.push({ key: "h:wacht", head: "Klantplan nog niet gekoppeld" });
    for (const [pid, its] of [...groups].sort((a, b) => kName(a[0]).localeCompare(kName(b[0])))) {
      const open = !!shown[pid] || !!qq;
      rows.push({
        key: "g:" + pid, cls: "p-kp-t", a: planName(pid), aSub: nOf(its.length, "taak wacht", "taken wachten") + " op een Clockify-project",
        b: "Klantplan nog niet gekoppeld", bSub: "koppel eerst " + planName(pid) + " aan een Clockify-project", pill: "Wacht", pillCls: OFF,
        acts: [
          { label: "Klantplan koppelen", primary: true, run: () => setPick({ kind: "plan", id: pid }) },
          ...(qq ? [] : [{ label: open ? "Verbergen" : "Tonen", title: open ? "Taken verbergen" : "Taken tonen", run: () => setShown((s) => ({ ...s, [pid]: !s[pid] })) }]),
        ],
      });
      if (open) for (const i of its.sort(byTitle)) rows.push({ key: i.r.taskId, cls: "p-kp-t p-kp-wait", a: i.r.title, aSub: sub(i), b: "Wacht op het klantplan", bSub: "daarna stelt de app een Clockify-taak voor" });
    }
    title = nOf(ready.length + waiting.length, "taak", "taken") + " zonder Clockify-taak";
  } else if (view === "linked") {
    const byKlant = (a: Item, b: Item) => a.klant.localeCompare(b.klant) || byTitle(a, b);
    const linked = list.filter((i) => i.r.linked).sort(byKlant), none = list.filter((i) => i.r.none).sort(byKlant);
    const change = (i: Item) => (i.r.planLinked ? [{ label: "Wijzigen", run: () => setPick({ kind: "task", id: i.r.taskId }) }] : []);
    for (const i of linked) {
      const l = i.r.linked!, gone = l.name === MISSING;
      rows.push({
        key: i.r.taskId, cls: "p-kp-t", a: i.r.title, aSub: sub(i),
        b: gone ? "Verwijderd in Clockify" : l.name, bCls: gone ? "" : "mono",
        bSub: gone ? "kies een andere Clockify-taak" : (l.estimate ? fmtHours(l.estimate) + " geschat" : "nog geen schatting in Clockify") + " · " + fmtHours(l.logged) + " gelogd"
          + (l.more ? " · + " + nOf(l.more, "andere Clockify-taak", "andere Clockify-taken") : ""),
        bSubCls: gone || !l.estimate ? "t-warn" : "", pill: gone ? "Weg" : "Gekoppeld", pillCls: gone ? WARN : OK,
        acts: [...change(i), { label: "Ontkoppelen", title: "De uren tellen dan niet meer mee; in Clockify blijft alles staan", run: () => linkOne(i.r, null) }],
      });
    }
    if (none.length) rows.push({ key: "h:geen", head: "Bewust zonder Clockify-taak" });
    for (const i of none) {
      rows.push({
        key: i.r.taskId, cls: "p-kp-t", a: i.r.title, aSub: sub(i), b: "Geen Clockify-taak", bSub: "telt niet mee in uren en bezetting", pill: "Geen", pillCls: OFF,
        acts: [...change(i), { label: "Terugzetten", title: "Terug naar Te koppelen", run: () => linkOne(i.r, null) }],
      });
    }
    title = nOf(linked.length, "taak", "taken") + " gekoppeld" + (none.length ? " · " + none.length + " bewust zonder" : "");
  } else {
    for (const i of [...list].sort(byTitle)) {
      rows.push({
        key: i.r.taskId, cls: "p-kp-t", a: i.r.title, aSub: sub(i), b: "Geen koppeling nodig", bSub: "klaar om af te sluiten volgens de labels uit Planner", pill: "Afsluiten", pillCls: OFF,
        acts: [i.r.planLinked
          ? { label: "Toch koppelen", run: () => setPick({ kind: "task", id: i.r.taskId }) }
          : { label: "Klantplan koppelen", run: () => setPick({ kind: "plan", id: i.r.projectId }) }],
      });
    }
    title = nOf(list.length, "taak", "taken") + " klaar om af te sluiten";
  }

  const runBulk = () => {
    const ls = bulk.map((i) => ({ taskId: i.r.taskId, clockifyTaskId: i.best!.clockifyTaskId as string | null }));
    const n = nOf(ls.length, "taak", "taken");
    save(
      async () => ({ ...(await linkAll(ls)), text: n + " gekoppeld op voorstel vanaf " + thr + " %" }),
      async () => ({ ...(await linkAll(ls.map((x) => ({ ...x, clockifyTaskId: null })))), text: "Ongedaan gemaakt: " + n + " weer zonder Clockify-taak" }),
    );
  };

  const kItems = [{ id: null as string | null, label: "Alle klanten" }, ...[...data.klanten].sort((a, b) => a.name.localeCompare(b.name)).map((k) => ({ id: k.id as string | null, label: k.name }))]
    .map((o) => ({ ...o, n: items.filter((i) => scoped(i, view) && (!o.id || i.r.projectId === o.id)).length }));
  const sub2 = view === "open" ? "Voorstellen op naam binnen het Clockify-project van de klant · hoogste zekerheid eerst"
    : view === "linked" ? "Ontkoppelen haalt de uren uit de bezetting; in Clockify blijft alles staan"
    : "Taken die klaar zijn om af te sluiten, hebben geen Clockify-taak nodig";
  const pickedItem = pick?.kind === "task" ? items.find((i) => i.r.taskId === pick.id) : undefined;
  const pickedPlan = pick?.kind === "plan" ? plans.get(pick.id) : undefined;

  return (
    <>
      <KoppelCard title={title} sub={sub2}
        tools={view === "open" && (
          <span className="p-kp-bulk">
            <select className="ex-input" aria-label="Zekerheid van de voorstellen" value={thr} onChange={(e) => setThr(Number(e.target.value))}>
              {THRESHOLDS.map((n) => <option key={n} value={n}>vanaf {n} %</option>)}
            </select>
            <button type="button" className="ex-btn ex-btn--primary" disabled={busy || !bulk.length} onClick={runBulk}>
              <Icon name="link" size={15} />{bulk.length ? nOf(bulk.length, "voorstel", "voorstellen") + " koppelen" : "Geen voorstellen"}
            </button>
          </span>
        )}
        bar={
          <div className="p-hbar p-kp-hbar">
            <div className="ex-seg" role="group" aria-label="Weergave">
              {VIEWS.map(([v, label]) => (
                <button key={v} type="button" aria-pressed={view === v} onClick={() => { setView(v); setConf("all"); }}>{label}<span className="p-segn">{count(v)}</span></button>
              ))}
            </div>
            <div className="p-ddwrap">
              <button type="button" className="ex-btn p-dd" aria-haspopup="listbox" aria-expanded={dd} onClick={() => setDd(!dd)}>Klant<span className="p-dd-v">{klant ? kName(klant) : "alle"}</span><Icon name="down" size={14} /></button>
              {dd && <>
                <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setDd(false)} />
                <div className="p-pop p-pop-s" role="listbox" aria-label="Klant kiezen">
                  <div className="p-pop-b">
                    {kItems.map((o) => (
                      <button key={o.id || "alle"} type="button" role="option" aria-selected={klant === o.id} className={"p-opt1" + (klant === o.id ? " on" : "")} onClick={() => { setKlant(o.id); setDd(false); }}>
                        <span className={"p-radio" + (klant === o.id ? " on" : "")} /><span className="nm">{o.label}</span><span className="c">{o.n}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>}
            </div>
            {view === "open" && (
              <div className="ex-seg" role="group" aria-label="Zekerheid van het voorstel">
                {CONF.map(([c, label]) => <button key={c} type="button" aria-pressed={conf === c} onClick={() => setConf(c)}>{label}</button>)}
              </div>
            )}
            {view !== "skip" && <button type="button" className={"p-tog p-tog-s" + (active ? " on" : "")} aria-pressed={active} title="Zonder geparkeerde taken" onClick={() => setActive(!active)}>Alleen actief</button>}
          </div>
        }
        notice={<ClockifyNotice state={links.clockify.state} error={links.clockify.error} />}
        foot="De app bewaart elke koppeling zelf; alleen gekoppelde taken tellen mee in uren en bezetting.">
        <KoppelList rows={rows} busy={busy} empty={qq ? "Niets gevonden voor " + q.trim() + "." : view === "open" ? "Niets meer te koppelen in deze lijst." : "Niets in deze lijst."} />
      </KoppelCard>
      {pickedItem && <TaskPicker key={pickedItem.r.taskId} i={pickedItem} links={links} people={people} onClose={() => setPick(null)} />}
      {pickedPlan && <PlanPicker key={pickedPlan.projectId} plan={pickedPlan} links={links} data={data} onClose={() => setPick(null)} />}
    </>
  );
}

/** Zelf kiezen voor één taak: een voorstel, een andere Clockify-taak in het project, een nieuwe, of bewust geen. */
function TaskPicker({ i, links, people, onClose }: { i: Item; links: ClockifyLinks; people: Map<string, { first: string }>; onClose: () => void }) {
  const { save, busy } = useKoppelSave();
  const [est, setEst] = useState<number | null>(8);
  const r = i.r, cur = r.linked?.clockifyTaskId || null;
  const proj = links.plans.find((p) => p.projectId === r.projectId)?.linked || null;
  const projName = proj ? projText(proj) : "het project";
  const choices = proj ? links.choices[proj.clockifyProjectId] || [] : [];

  const opts: PickOpt[] = [], seen = new Set<string>();
  const add = (o: PickOpt) => { if (!seen.has(o.key)) { seen.add(o.key); opts.push(o); } };
  if (r.linked) add({ key: r.linked.clockifyTaskId, label: r.linked.name, note: "nu gekoppeld · " + fmtHours(r.linked.logged) + " gelogd", pill: "Gekoppeld", pillCls: OK, group: "Nu gekoppeld" });
  for (const s of r.suggestions) {
    const pc = pctOf(s.score), sup = isSupport(s.name);
    add({ key: s.clockifyTaskId, label: s.name, note: sup ? SUPPORT_NOTE : "voorstel op naam" + (s.estimate ? " · " + fmtHours(s.estimate) + " geschat" : ""), pill: pc + " %", pillCls: scorePill(pc), disabled: sup, group: "Voorstellen op naam" });
  }
  // een Clockify-taak die al bij een andere taak hoort, mag ook (n-op-n); enkel supporttaken niet
  const blocked = (c: (typeof choices)[number]) => isSupport(c.name);
  for (const c of choices.filter((c) => !blocked(c))) {
    add({ key: c.clockifyTaskId, label: c.name, note: (c.estimate ? fmtHours(c.estimate) + " geschat · " : "") + (c.used ? "hangt ook aan een andere Planner-taak" : "nog aan geen Planner-taak gekoppeld"), group: "Andere taken in " + (proj?.name || "het project") });
  }
  const assignees = (i.t?.owners || []).filter((o) => links.people.some((p) => p.personId === o.personId)).map((o) => people.get(o.personId)?.first || "?");
  add({ key: NEW, label: "Nieuwe Clockify-taak: " + r.title, note: "in " + projName + (assignees.length ? ", met " + assignees.join(", ") + " als toegewezene" : ", zonder toegewezene"), keep: true, group: "Zonder bestaande Clockify-taak" });
  add({ key: NONE, label: "Geen Clockify-taak", note: "bewust: telt niet mee in uren en bezetting", keep: true, group: "Zonder bestaande Clockify-taak" });
  const used = choices.filter((c) => !seen.has(c.clockifyTaskId) && blocked(c));
  for (const c of used) add({ key: c.clockifyTaskId, label: c.name, note: SUPPORT_NOTE, disabled: true, group: "Niet te kiezen" });

  const confirm = (sel: string | null) => {
    if (!sel || sel === cur || (sel === NONE && r.none)) { onClose(); return; }
    const done = (ok: boolean) => { if (ok) onClose(); };
    if (sel === NEW) {
      // een gekoppelde taak eerst loskoppelen, anders houdt de server de bestaande; mislukt het maken, dan terugzetten
      save(async () => {
        if (cur) await clockifyApi.linkTasks([{ taskId: r.taskId, clockifyTaskId: null }]);
        try { return await clockifyApi.createTask(r.taskId, est ?? undefined); } catch (e) {
          if (cur) await clockifyApi.linkTasks([{ taskId: r.taskId, clockifyTaskId: cur }]).catch(() => {});
          throw e;
        }
      }).then(done);
      return;
    }
    const to = sel === NONE ? "" : sel;
    save(() => clockifyApi.linkTasks([{ taskId: r.taskId, clockifyTaskId: to }]), () => clockifyApi.linkTasks([{ taskId: r.taskId, clockifyTaskId: prevOf(r) }])).then(done);
  };

  return (
    <KoppelPicker eyebrow={"Taak koppelen · " + i.klant} title={r.title}
      state={r.linked ? ["Gekoppeld aan " + r.linked.name, OK] : r.none ? ["Bewust geen Clockify-taak", OFF] : ["Nog niet gekoppeld", WARN]}
      kv={[["Klant", i.klant], ["Eigenaar", i.who || "–"], ["Deadline", i.t ? ddmm(i.t.end) : "–"], ["Clockify-project", proj ? projText(proj) : "nog niet gekoppeld"]]}
      listTitle={"Kies een Clockify-taak in " + (proj?.name || "het project")} options={opts}
      initial={cur || (r.none ? NONE : i.best?.clockifyTaskId || NEW)}
      note={(r.linked?.more ? "Deze taak heeft " + (r.linked.more + 1) + " Clockify-taken; uw keuze vervangt ze. " : "") + "Gelogde uren blijven in Clockify; de app bewaart alleen de koppeling."}
      confirmText={(sel) => (sel === NEW ? "Aanmaken en koppelen" : sel === NONE ? "Opslaan" : "Koppelen")}
      busy={busy} onConfirm={confirm} onClose={onClose}
      extra={(sel) => sel === NEW && (
        <div className="p-kp-est"><span className="ex-label">Schatting in Clockify</span>
          <div className="p-quick">
            {EST.map((h) => <button key={h} type="button" className={"p-tog p-tog-s" + (est === h ? " on" : "")} aria-pressed={est === h} onClick={() => setEst(h)}>{h} u</button>)}
            <button type="button" className={"p-tog p-tog-s" + (est == null ? " on" : "")} aria-pressed={est == null} onClick={() => setEst(null)}>later</button>
          </div>
        </div>
      )} />
  );
}
